import { useState } from "react";

import { css } from "@compiled/react";

import Avatar from "@atlaskit/avatar";
import Link from "@atlaskit/link";
import Skeleton from "@atlaskit/skeleton";
import { token } from "@atlaskit/tokens";

import { ExpandGroupButton } from "./expand-group-button";
import { formatRelativeTime } from "./format-relative-time";
import { ReadStateIndicator } from "./read-state-indicator";
import { NotificationGroup as NotificationGroupType } from "./types";

const GroupContainerStyle = css({
    marginBlockEnd: token("space.200")
});

const GroupTitleStyle = css({
    fontFamily:
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    fontSize: "12px",
    fontWeight: 600,
    color: token("color.text.subtlest"),
    marginBlockEnd: token("space.075"),
    marginBlockStart: 0,
    marginInlineStart: token("space.100")
});

const NotificationItemStyle = css({
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    marginBlockEnd: token("space.200"),
    borderRadius: token("border.radius.100")
});

const NotificationItemContentStyle = css({
    "cursor": "pointer",
    "display": "flex",
    "alignItems": "flex-start",
    "width": "100%",
    "padding": token("space.100"),
    "marginBlockEnd": "16px",
    "position": "relative",
    "&:hover": {
        backgroundColor: token("color.background.neutral.subtle.hovered")
    }
});

const AvatarContainerStyle = css({
    marginInlineEnd: token("space.100")
});

const IconContainerStyle = css({
    marginInlineEnd: token("space.100")
});

const firstNotificationStyle = css({
    margin: 0,
    fontFamily:
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    fontSize: "14px",
    fontWeight: 500
});

const timestampStyle = css({
    fontFamily:
        "'Atlassian Sans', ui-sans-serif, -apple-system, 'system-ui', 'Segoe UI', Ubuntu, 'Helvetica Neue', sans-serif",
    fontSize: "14px",
    color: token("color.text.subtlest"),
    marginInlineStart: token("space.050")
});
const NestedSkeletonItemStyle = css({
    display: "flex",
    alignItems: "center",
    padding: token("space.100"),
    width: "100%"
});

const NestedAvatarSkeletonStyle = css({
    marginInlineEnd: token("space.100"),
    borderRadius: "50%"
});

const NestedTextSkeletonStyle = css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    paddingInlineStart: token("space.100")
});

const nestedNotificationStyle = css({
    "cursor": "pointer",
    "position": "relative",
    "display": "flex",
    "alignItems": "center",
    "marginBlockEnd": token("space.200"),
    "padding": token("space.100"),
    "width": "100%",
    "&:hover": {
        backgroundColor: token("color.background.neutral.subtle.hovered")
    }
});

const UpperAvatarThreadStyle = css({
    position: "absolute",
    insetInlineStart: "29px",
    insetBlockStart: "-15px",
    width: "2px",
    height: "20px",
    backgroundColor: token("color.border")
});

const LowerAvatarThreadStyle = css({
    position: "absolute",
    insetInlineStart: "29px",
    insetBlockStart: "55px",
    width: "2px",
    height: "calc(100% - 54px)",
    backgroundColor: token("color.border")
});

interface NotificationGroupProps {
    title: string;
    notificationGroups: NotificationGroupType[];
    onExpand: (groupId: string, continuationToken?: string) => void;
    loadingGroupId?: string | null;
}

const NotificationGroup = ({
    title,
    notificationGroups,
    onExpand,
    loadingGroupId
}: NotificationGroupProps) => {
    const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    return (
        <div css={GroupContainerStyle}>
            <h5 css={GroupTitleStyle}>{title}</h5>
            {notificationGroups.map(
                // eslint-disable-next-line complexity
                (group) => {
                    const firstNotification = group.notifications[0];
                    const isExpanded = expandedGroupId === group.id;

                    if (!firstNotification) return null;

                    const isRead = firstNotification.readState === "read";

                    const hasMoreNotifications =
                        group.additionalActors &&
                        group.additionalActors.length > 0;

                    return (
                        <div key={group.id} css={NotificationItemStyle}>
                            {/* Main notification content */}
                            <div
                                css={NotificationItemContentStyle}
                                onMouseEnter={() =>
                                    setHoveredId(firstNotification.id)
                                }
                                onMouseLeave={() => setHoveredId(null)}
                                onFocus={() =>
                                    setHoveredId(firstNotification.id)
                                }
                                onBlur={() => setHoveredId(null)}
                            >
                                <div css={AvatarContainerStyle}>
                                    <Avatar
                                        size="large"
                                        src={
                                            firstNotification.content
                                                .actors?.[0]?.avatarUrl
                                        }
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <p css={firstNotificationStyle}>
                                        {firstNotification.content.message}
                                        <span css={timestampStyle}>
                                            {" "}
                                            {formatRelativeTime(
                                                firstNotification.timestamp
                                            )}
                                        </span>
                                    </p>
                                    <div
                                        css={css({
                                            display: "flex",
                                            alignItems: "center"
                                        })}
                                    >
                                        <div css={IconContainerStyle}>
                                            {/* eslint-disable-next-line no-restricted-syntax */}
                                            <img
                                                src={
                                                    firstNotification.content
                                                        .entity?.icon?.url
                                                }
                                                height={16}
                                                width={16}
                                                alt={
                                                    firstNotification.content
                                                        .entity?.title
                                                }
                                            />
                                        </div>
                                        <Link
                                            appearance="subtle"
                                            href={
                                                firstNotification.content.entity
                                                    ?.link?.url
                                            }
                                        >
                                            {
                                                firstNotification.content.entity
                                                    ?.title
                                            }
                                        </Link>
                                    </div>
                                </div>
                                <ReadStateIndicator
                                    isRead={isRead}
                                    isCardHovered={
                                        firstNotification.id === hoveredId
                                    }
                                    onToggle={() => {
                                        // TODO: Implement read state toggle handler
                                        console.log(
                                            "Toggle read state for notification:",
                                            firstNotification.id
                                        );
                                    }}
                                />
                                {isExpanded && (
                                    <div
                                        className="lower-avatar-thread"
                                        css={LowerAvatarThreadStyle}
                                    />
                                )}
                            </div>

                            {isExpanded && (
                                <>
                                    {loadingGroupId === group.id && (
                                        <>
                                            {[...Array(5)].map((_, index) => (
                                                <div
                                                    key={`skeleton-${index}`}
                                                    css={
                                                        NestedSkeletonItemStyle
                                                    }
                                                    style={{
                                                        position: "relative"
                                                    }}
                                                >
                                                    {index === 0 && (
                                                        <div
                                                            className="upper-avatar-thread"
                                                            css={
                                                                UpperAvatarThreadStyle
                                                            }
                                                        />
                                                    )}
                                                    <div
                                                        css={
                                                            NestedAvatarSkeletonStyle
                                                        }
                                                    >
                                                        <Skeleton
                                                            width="44px"
                                                            height="44px"
                                                        />
                                                    </div>
                                                    <div
                                                        css={
                                                            NestedTextSkeletonStyle
                                                        }
                                                    >
                                                        <Skeleton
                                                            width="50%"
                                                            height="12px"
                                                        />
                                                        <Skeleton
                                                            width="70%"
                                                            height="12px"
                                                        />
                                                    </div>
                                                    {index < 4 && (
                                                        <div
                                                            className="lower-avatar-thread"
                                                            css={
                                                                LowerAvatarThreadStyle
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </>
                                    )}
                                    {loadingGroupId !== group.id &&
                                        group.notifications
                                            .slice(1)
                                            .map((notification, index) => (
                                                <div
                                                    key={notification.id}
                                                    css={
                                                        nestedNotificationStyle
                                                    }
                                                    onMouseEnter={() =>
                                                        setHoveredId(
                                                            notification.id
                                                        )
                                                    }
                                                    onMouseLeave={() =>
                                                        setHoveredId(null)
                                                    }
                                                    onFocus={() =>
                                                        setHoveredId(
                                                            notification.id
                                                        )
                                                    }
                                                    onBlur={() =>
                                                        setHoveredId(null)
                                                    }
                                                >
                                                    <div
                                                        className="upper-avatar-thread"
                                                        css={
                                                            UpperAvatarThreadStyle
                                                        }
                                                    />
                                                    <div
                                                        css={
                                                            AvatarContainerStyle
                                                        }
                                                    >
                                                        <Avatar
                                                            size="large"
                                                            src={
                                                                notification
                                                                    .content
                                                                    .actors?.[0]
                                                                    ?.avatarUrl
                                                            }
                                                        />
                                                    </div>
                                                    <div
                                                        style={{
                                                            flex: 1
                                                        }}
                                                    >
                                                        <p
                                                            css={
                                                                firstNotificationStyle
                                                            }
                                                        >
                                                            {
                                                                notification
                                                                    .content
                                                                    .message
                                                            }
                                                            <span
                                                                css={
                                                                    timestampStyle
                                                                }
                                                            >
                                                                {" "}
                                                                {formatRelativeTime(
                                                                    notification.timestamp
                                                                )}
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <ReadStateIndicator
                                                        isRead={
                                                            notification.readState ===
                                                            "read"
                                                        }
                                                        isCardHovered={
                                                            notification.id ===
                                                            hoveredId
                                                        }
                                                        onToggle={() => {
                                                            // TODO: Implement read state toggle handler
                                                            console.log(
                                                                "Toggle read state for notification:",
                                                                notification.id
                                                            );
                                                        }}
                                                    />
                                                    {index <
                                                        group.notifications
                                                            .length -
                                                            2 && (
                                                        <div
                                                            className="lower-avatar-thread"
                                                            css={
                                                                LowerAvatarThreadStyle
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                </>
                            )}

                            {/* Collapsed group for additional actors - nested inside the card */}
                            {hasMoreNotifications && (
                                <ExpandGroupButton
                                    additionalActors={group.additionalActors}
                                    notificationCount={group.size - 1}
                                    isExpanded={isExpanded}
                                    onToggle={() => {
                                        if (isExpanded) {
                                            setExpandedGroupId(null);
                                        } else {
                                            setExpandedGroupId(group.id);
                                            onExpand(
                                                group.id,
                                                group.notificationContinuationToken
                                            );
                                        }
                                    }}
                                />
                            )}
                        </div>
                    );
                }
            )}
        </div>
    );
};
export default NotificationGroup;
